import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  PencilIcon,
  TrashIcon,
  ArrowLeftIcon,
  BuildingOfficeIcon,
  EnvelopeIcon,
  PhoneIcon,
  GlobeAltIcon,
  MapPinIcon,
  CalendarIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';
import { companiesAPI } from '../../services/api';
import { Company, CompanyStatus } from '../../types';
import { useTranslation } from 'react-i18next';

const CompanyDetail: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadCompany();
    }
  }, [id]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await companiesAPI.getById(id!);
      setCompany(data);
    } catch (err) {
      setError(t('companyForm.errorLoad'));
      console.error('Error loading company:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!company) return;
    
    if (window.confirm(t('companies.confirmDelete'))) {
      try {
        await companiesAPI.delete(company.id);
        navigate('/companies');
      } catch (err) {
        setError(t('companies.delete'));
        console.error('Error deleting company:', err);
      }
    }
  };

  const getStatusBadge = (status: CompanyStatus) => {
    const statusConfig = {
      [CompanyStatus.ACTIVE]: 'bg-success-100 text-success-800 border-success-200',
      [CompanyStatus.INACTIVE]: 'bg-gray-100 text-gray-800 border-gray-200',
      [CompanyStatus.SUSPENDED]: 'bg-danger-100 text-danger-800 border-danger-200',
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${statusConfig[status]}`}>
        <div className={`w-2 h-2 rounded-full mr-2 ${
          status === CompanyStatus.ACTIVE ? 'bg-success-500' :
          status === CompanyStatus.INACTIVE ? 'bg-gray-500' : 'bg-danger-500'
        }`}></div>
        {t(`companies.status.${status}`)}
      </span>
    );
  };

  const formatDate = (date: Date | string) => {
    return new Date(date).toLocaleDateString(i18n.language || 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className="bg-danger-50 border border-danger-200 rounded-xl p-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-danger-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-danger-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-danger-800">Error loading company</h3>
            <p className="text-sm text-danger-700 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-4">
            <Link
              to="/companies"
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{company.name}</h1>
              <p className="mt-1 text-sm text-gray-600">{t('companies.details')}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <Link
              to={`/companies/${company.id}/edit`}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
            >
              <PencilIcon className="h-4 w-4 mr-2" />
              {t('users.edit')}
            </Link>
            <button
              onClick={handleDelete}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-danger-600 hover:bg-danger-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-danger-500 transition-all duration-200"
            >
              <TrashIcon className="h-4 w-4 mr-2" />
              {t('users.delete')}
            </button>
          </div>
        </div>
      </div>

      {/* Company Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Basic Information */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('companies.basicInfo')}</h2>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center">
                    <BuildingOfficeIcon className="h-6 w-6 text-white" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg font-medium text-gray-900">{company.name}</h3>
                  <div className="mt-1">
                    {getStatusBadge(company.status)}
                  </div>
                  {company.description && (
                    <p className="mt-2 text-sm text-gray-600">{company.description}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="flex items-center space-x-3">
                  <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{t('companies.email')}</p>
                    <p className="text-sm text-gray-600">{company.email}</p>
                  </div>
                </div>

                {company.phone && (
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('companies.phone')}</p>
                      <p className="text-sm text-gray-600">{company.phone}</p>
                    </div>
                  </div>
                )}

                {company.website && (
                  <div className="flex items-center space-x-3">
                    <GlobeAltIcon className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">{t('companies.website')}</p>
                      <a 
                        href={company.website} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-sm text-primary-600 hover:text-primary-700"
                      >
                        {company.website}
                      </a>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Address Information */}
          {(company.address || company.city || company.state || company.country || company.postalCode) && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('companies.addressInfo')}</h2>
              <div className="flex items-start space-x-3">
                <MapPinIcon className="h-5 w-5 text-gray-400 mt-0.5" />
                <div className="text-sm text-gray-600">
                  {company.address && <p>{company.address}</p>}
                  {(company.city || company.state || company.postalCode) && (
                    <p>
                      {[company.city, company.state, company.postalCode].filter(Boolean).join(', ')}
                    </p>
                  )}
                  {company.country && <p>{company.country}</p>}
                </div>
              </div>
            </div>
          )}

          {/* Users Section */}
          {company.users && company.users.length > 0 && (
            <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Company Users</h2>
              <div className="space-y-3">
                {company.users.map((user) => (
                  <div key={user.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                        <span className="text-sm font-medium text-gray-700">
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {user.firstName} {user.lastName}
                      </p>
                      <p className="text-sm text-gray-600">{user.email}</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                        {user.role}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Company Details */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('companies.details')}</h2>
            <div className="space-y-4">
              <div>
                <p className="text-sm font-medium text-gray-900">{t('companyForm.status')}</p>
                <div className="mt-1">
                  {getStatusBadge(company.status)}
                </div>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900">{t('companies.created')}</p>
                <p className="text-sm text-gray-600">{formatDate(company.createdAt)}</p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-900">{t('companies.updated')}</p>
                <p className="text-sm text-gray-600">{formatDate(company.updatedAt)}</p>
              </div>

              {company.users && (
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('users.title')}</p>
                  <div className="flex items-center mt-1">
                    <UserGroupIcon className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-600">{company.users.length}</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('companies.quickActions')}</h2>
            <div className="space-y-3">
              <Link
                to={`/companies/${company.id}/edit`}
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg shadow-soft text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <PencilIcon className="h-4 w-4 mr-2" />
                {t('companies.edit')}
              </Link>
              <Link
                to="/companies"
                className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg shadow-soft text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-all duration-200"
              >
                <ArrowLeftIcon className="h-4 w-4 mr-2" />
                {t('companies.back')}
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanyDetail;
